import { useEffect, useRef, useState } from "react";
import {
  OrderStatusType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { FaPlus } from "react-icons/fa";
import { OrderStatusService } from "../../services/order_status.service";
import SelectColor from "../../components/admin-pages/products/select-color/SelectColor";

const OrderStatus = () => {
  const [groupNames, setGroupNames] = useState<OrderStatusType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<OrderStatusType | null>(
    null
  );
  const [selectedColorName, setSelectedColorName] = useState<
    string | undefined
  >(undefined);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const initialGroupNamesValues = useRef({
    name: "",
    bg_color: 0,
    translations: [] as { lang_code: string; name: string }[],
  });
  useEffect(() => {
    setLoading(true);
    OrderStatusService.getAllStatus()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: OrderStatusType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setGroupNames(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await OrderStatusService.getAllStatus();
    setGroupNames(
      updatedData.data?.data?.map((item: OrderStatusType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        name: "",
        bg_color: 0,
        translations: [] as { lang_code: string; name: string }[],
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Status tələb olunur"),
        bg_color: Yup.string().required("Rəng tələb olunur"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          name: values.name,
        };
        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];
        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing && editingRecord) {
          const isEqual =
            JSON.stringify(values.name) ===
              JSON.stringify(initialGroupNamesValues.current.name) &&
            JSON.stringify(values.bg_color) ===
              JSON.stringify(initialGroupNamesValues.current.bg_color) &&
            JSON.stringify(values.translations) ===
              JSON.stringify(initialGroupNamesValues.current.translations);
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const response = await OrderStatusService.updateStatus({
              ...finalValues,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Status uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Statusu yeniləmək alınmadı."
              );
            }
          }
        } else {
          const response = await OrderStatusService.addStatus(finalValues);
          if (response.status === 201) {
            message.success("Status uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Statusu əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          name: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, name: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: OrderStatusType) => {
    try {
      await OrderStatusService.deleteStatus(record.id.toString());
      setGroupNames((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Status uğurla silindi!");
    } catch (error) {
      message.error("Statusu silmək alınmadı.");
    }
  };
  const columns: TableProps<OrderStatusType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Status",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Rəng",
      dataIndex: "color_name",
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu statusu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    setSelectedColorName(undefined);
    resetForm();
    setLangCode("AZ");
  };
  
  const handleEditClick = (record: OrderStatusType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("bg_color", record.bg_color);
    setFieldValue("translations", record.translations || []);
    setSelectedColorName(record.color_name);
    setLangCode("AZ");
    initialGroupNamesValues.current = {
      name: record.name,
      bg_color: record.bg_color,
      translations: record.translations || [],
    };
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Statuslar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Sifarişlərin statusları</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<OrderStatusType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={groupNames}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Statusu redaktə edin" : "Yeni Status əlavə edin"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedColorName(undefined);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Status {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Status"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.name || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>

            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <SelectColor
              setFieldValue={setFieldValue}
              color_id={editingRecord?.bg_color ?? null}
              selectedName={selectedColorName}
              setSelectedName={setSelectedColorName}
              field={"bg_color"}
            />
            <ErrorMessage error={errors.bg_color} touched={touched.bg_color} />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? " Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default OrderStatus;
